import React, { useEffect, useState, Component } from 'react';
import { NavigationProps } from '../types/navigation';
import { Button } from '../components/ui/Button';
import { Clock, CheckCircle2, X, KeyRound, Star, CalendarPlus, Download, Copy, Check, QrCode } from 'lucide-react';
import { useStore, allowsCodeRedemption, allowsQrRedemption } from '../lib/store';
import { Input } from '../components/ui/Input';
import { CatchQrReveal } from '../components/CatchQrReveal';
import { googleCalendarUrl, downloadIcs, dropDetailsText } from '../lib/calendar';
type CatchFilter = 'All' | 'Reserved' | 'To review' | 'Reviewed';
export function CatchesPage({
  onNavigate
}: NavigationProps) {
  const {
    reservations,
    events,
    redeemReservationByCode
  } = useStore();
  const [filter, setFilter] = useState<CatchFilter>('All');
  const [activeRedemptionId, setActiveRedemptionId] = useState<string | null>(null);
  const [redemptionStep, setRedemptionStep] = useState<'confirm' | 'exchange' | 'success'>('confirm');
  const [inputCode, setInputCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [timeLeft, setTimeLeft] = useState(7200);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // The reservation currently shown as a full-screen, gamified catch pass.
  const [passReservationId, setPassReservationId] = useState<string | null>(null);
  const copyDetails = async (event: (typeof events)[number]) => {
    try {
      await navigator.clipboard.writeText(dropDetailsText(event));
      setCopiedId(event.id);
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      setCopiedId(null);
    }
  };
  // Timer logic
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (activeRedemptionId && redemptionStep === 'exchange') {
      timer = setInterval(() => {
        setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [activeRedemptionId, redemptionStep]);
  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  const startRedemption = (id: string) => {
    setActiveRedemptionId(id);
    setRedemptionStep('confirm');
  };
  const confirmRedemption = () => {
    setRedemptionStep('exchange');
  };
  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeRedemptionId) return;
    // A reserved spot only converts into a real catch when the user enters the
    // drop code handed to them at pickup. The store validates it against the
    // drop's admin code and tags the catch as a recurring (reserved-spot) pickup.
    const ok = redeemReservationByCode(activeRedemptionId, inputCode);
    if (ok) {
      setCodeError(false);
      setRedemptionStep('success');
    } else {
      setCodeError(true);
      setInputCode('');
    }
  };
  const closeRedemption = () => {
    setActiveRedemptionId(null);
    setRedemptionStep('confirm');
    setInputCode('');
    setCodeError(false);
  };
  const activeReservation = reservations.find(r => r.id === activeRedemptionId);
  const activeEvent = activeReservation ? events.find(e => e.id === activeReservation.eventId) : undefined;
  const method = activeEvent?.verificationMethod;
  const codeAllowed = allowsCodeRedemption(method);
  const qrAllowed = allowsQrRedemption(method);
  // The user's reservation QR (their user code) for staff to scan at pickup.
  const reservationQrSrc = activeReservation ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(activeReservation.userCode)}` : '';
  return <div className="min-h-screen bg-brand-ice pb-32" data-id="element-1753" data-mp="2vkg62exh6jba">
      <div className="px-6 pt-5 pb-5 bg-brand-ice/95 backdrop-blur sticky top-0 z-10" data-id="element-1754" data-mp="2vivb4o790ycw">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-purple mb-1" data-id="element-1755" data-mp="30czkfe3zlih5">Your collection</p>
        <h1 className="text-4xl font-display text-brand-navy tracking-tight" data-id="element-1756" data-mp="1cla640y758t8">
          My Catches
        </h1>
        <div className="flex space-x-2 mt-4 overflow-x-auto pb-2 scrollbar-hide" data-id="element-1757" data-mp="12p754yf2zg7v">
          {(['All', 'Reserved', 'To review', 'Reviewed'] as CatchFilter[]).map(f => {
          const count = f === 'All' ? reservations.length : f === 'Reserved' ? reservations.filter(r => r.status === 'reserved').length : f === 'To review' ? reservations.filter(r => r.status === 'redeemed').length : reservations.filter(r => r.status === 'reviewed').length;
          const active = filter === f;
          return <button key={f} onClick={() => setFilter(f)} aria-pressed={active} className={`px-4 py-2 rounded-full text-xs font-bold transition-colors tracking-wide whitespace-nowrap ${active ? 'bg-brand-purple text-white shadow-[0_5px_12px_rgba(96,70,232,0.2)]' : 'bg-white hover:bg-brand-lavender text-brand-navy/65'}`} data-id="element-1758" data-mp="29ddr51qbaenp">
                  {f} ({count})
                </button>;
        })}
        </div>
      </div>

      <div className="p-4 space-y-4" data-id="element-1759" data-mp="30kmai3lk5gh4">
        {/* Instructions */}
        <div className="bg-brand-lavender rounded-[1.75rem] p-6 border border-white shadow-[0_12px_30px_rgba(22,38,95,0.06)] mb-5" data-id="element-1760" data-mp="34lxsbmy6wxer">
          <h2 className="text-xl font-display text-brand-navy mb-1" data-id="element-1761" data-mp="39dzb7rpv5mdr">
            Your Catch History
          </h2>
          <p className="text-brand-navy/65 text-sm" data-id="element-1762" data-mp="3een12a2cvx6e">
            Review a redeemed catch to earn points and unlock your next drop.
          </p>
        </div>

        {reservations.length === 0 ? <div className="text-center py-16 px-4" data-id="element-1763" data-mp="3o8m4dce61mzx">
            <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1764" data-mp="2ylrn5z6pem5v">
              <span className="text-4xl" data-id="element-1765" data-mp="2tosvdnaiirau">🎣</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1766" data-mp="38cw4v6nrkl4e">
              No catches yet
            </h3>
            <p className="text-gray-500 mb-6" data-id="element-1767" data-mp="33le0s9qg24f7">
              Check the feed to find upcoming drops near you.
            </p>
            <Button variant="secondary" onClick={() => onNavigate('feed')} data-id="element-1768" data-mp="3hbhicnedp9yb">
              Find Drops
            </Button>
          </div> : reservations.filter(res => filter === 'All' ? true : filter === 'Reserved' ? res.status === 'reserved' : filter === 'To review' ? res.status === 'redeemed' : res.status === 'reviewed').map(res => {
        const event = events.find(e => e.id === res.eventId);
        if (!event) return null;
        return <div key={res.id} className="bg-white rounded-[1.75rem] p-5 shadow-[0_14px_34px_rgba(22,38,95,0.08)] border border-white transition-all hover:-translate-y-0.5" data-id="element-1769" data-mp="cfohgwxijzar">
                  <div className="flex justify-between items-start mb-4" data-id="element-1770" data-mp="2o29d9gh2gc8">
                    <div className="flex items-start space-x-3" data-id="element-1771" data-mp="3kuqev0ffp5if">
                      {event.brandLogo && <div className="w-12 h-12 rounded-2xl bg-brand-ice border border-brand-lavender flex items-center justify-center p-2 shrink-0" data-id="element-1772" data-mp="3oxmpa3utlvba">
                          <img src={event.brandLogo} alt={event.brand} className="max-w-full max-h-full object-contain mix-blend-multiply" data-id="element-1773" data-mp="3az54adwauwx2" />
                        </div>}
                      <div data-id="element-1774" data-mp="xny8g6ugsx0">
                        <span className={`text-[10px] font-black px-3 py-1.5 rounded-full tracking-wider ${res.status === 'reviewed' ? 'bg-green-100 text-green-700' : res.status === 'redeemed' ? 'bg-yellow-100 text-yellow-800' : 'bg-brand-lavender text-brand-purple'}`} data-id="element-1775" data-mp="xxe3ay6djoz">
                          {res.status === 'redeemed' ? 'Awaiting review' : res.status === 'reviewed' ? 'Reviewed ✓' : 'Reserved'}
                        </span>
                        <h3 className="font-bold text-gray-900 text-lg mt-2 leading-tight" data-id="element-1776" data-mp="35b401h4gaw55">
                          {event.brand}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-1777" data-mp="30f5hwb9puad6">{event.title}</p>
                      </div>
                    </div>
                  </div>

                  {res.status === 'reserved' && <div className="space-y-3" data-id="element-1778" data-mp="164yns711czpu">
                      {/* Primary identification path — a big, gamified catch
                       pass the user shows to staff to scan & validate. */}
                      {allowsQrRedemption(event.verificationMethod) && <button type="button" onClick={() => setPassReservationId(res.id)} className="w-full flex items-center gap-4 rounded-[1.5rem] bg-brand-purple text-white p-4 text-left shadow-[0_10px_22px_rgba(96,70,232,0.22)] transition-transform hover:scale-[1.01] active:scale-[0.99]" data-id="element-1779" data-mp="1fwuyqio80kbh">
                          <span className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shrink-0 p-1.5" data-id="element-1780" data-mp="x6d8qlurw2tt">
                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=2&data=${encodeURIComponent(res.userCode)}`} alt="" aria-hidden="true" className="w-full h-full object-contain" data-id="element-1781" data-mp="28jfo5dl4ymce" />
                          </span>
                          <span className="flex-1" data-id="element-1782" data-mp="f2vl6tjj47uu">
                            <span className="block text-[11px] font-bold uppercase tracking-wider text-brand-yellow" data-id="element-1783" data-mp="f46t95k0c1rr">
                              Your catch pass
                            </span>
                            <span className="block font-display text-lg leading-tight" data-id="element-1784" data-mp="3ikvvwgggyrc5">
                              Show & get scanned
                            </span>
                            <span className="block text-xs text-white/60" data-id="element-1785" data-mp="3shiib9og1qga">
                              Tap to present to staff at {event.brand}
                            </span>
                          </span>
                          <QrCode className="w-6 h-6 text-brand-yellow shrink-0" data-id="element-1786" data-mp="17qhr48g8bokv" />
                        </button>}
                      <Button onClick={() => startRedemption(res.id)} className="w-full shadow-lg shadow-brand-purple/20" data-id="element-1787" data-mp="1csqk34t3qqmz">
                        <KeyRound className="w-4 h-4 mr-2" data-id="element-1788" data-mp="2sboici7adjoq" />
                        Redeem Now
                      </Button>

                      {/* Save this reserved drop to a calendar */}
                      <div className="grid grid-cols-3 gap-2" data-id="element-1789" data-mp="2ijihsk2yxtxx">
                        <a href={googleCalendarUrl(event)} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center gap-1 bg-brand-light hover:bg-gray-200 text-brand-blue rounded-xl py-2.5 px-1 text-[11px] font-bold transition-colors" data-id="element-1790" data-mp="2dlqoblmodzrm">
                          <CalendarPlus className="w-4 h-4" data-id="element-1791" data-mp="2rl5rw4g7eo7" />
                          Google
                        </a>
                        <button type="button" onClick={() => downloadIcs(event)} className="flex flex-col items-center justify-center gap-1 bg-brand-light hover:bg-gray-200 text-brand-blue rounded-xl py-2.5 px-1 text-[11px] font-bold transition-colors" data-id="element-1792" data-mp="3tzgxv7wkdg8n">
                          <Download className="w-4 h-4" data-id="element-1793" data-mp="1na5bt3foyvrd" />
                          .ics file
                        </button>
                        <button type="button" onClick={() => copyDetails(event)} className="flex flex-col items-center justify-center gap-1 bg-brand-light hover:bg-gray-200 text-brand-blue rounded-xl py-2.5 px-1 text-[11px] font-bold transition-colors" data-id="element-1794" data-mp="2b1kjlbq8m5a0">
                          {copiedId === event.id ? <Check className="w-4 h-4 text-green-600" data-id="element-1795" data-mp="pld99fm7n7mj" /> : <Copy className="w-4 h-4" data-id="element-1796" data-mp="unaycqhakwne" />}
                          {copiedId === event.id ? 'Copied' : 'Copy info'}
                        </button>
                      </div>
                    </div>}

                  {res.status === 'redeemed' && <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4" data-id="element-1797" data-mp="3hsclhkcuayr6">
                      <p className="text-sm font-bold text-yellow-800 mb-3" data-id="element-1798" data-mp="3dqr3jklg7ua4">
                        Review required to unlock next drop
                      </p>
                      <Button onClick={() => onNavigate('review-flow')} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none" data-id="element-1799" data-mp="2l592oqydvily">
                        <Star className="w-4 h-4 mr-2" data-id="element-1800" data-mp="2u3b8djt2azfk" />
                        Review Catch (4 items)
                      </Button>
                    </div>}

                  {res.status === 'reviewed' && <div className="flex items-center justify-center text-green-700 bg-green-50 border border-green-200 rounded-xl text-sm font-bold py-2.5" data-id="element-1801" data-mp="2f2xpqutyhqns">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" data-id="element-1802" data-mp="2k2k2z49sb6wl" />
                      Review completed
                    </div>}
                </div>;
      })}

        {reservations.length > 0 && reservations.filter(res => filter === 'All' ? true : filter === 'Reserved' ? res.status === 'reserved' : filter === 'To review' ? res.status === 'redeemed' : res.status === 'reviewed').length === 0 && <div className="text-center py-12 text-gray-500" data-id="element-1803" data-mp="3cpmyron2t3j5">
              <p className="font-medium" data-id="element-1804" data-mp="1ddk7vtc4h6vg">Nothing here yet</p>
              <p className="text-sm mt-1" data-id="element-1805" data-mp="1df521huiynss">No catches match “{filter}”.</p>
            </div>}
      </div>

      {/* Full-screen gamified catch pass — celebration → instruction slide */}
      {passReservationId && (() => {
      const passRes = reservations.find(r => r.id === passReservationId);
      const passEvent = passRes ? events.find(e => e.id === passRes.eventId) : undefined;
      if (!passRes || !passEvent) return null;
      return <CatchQrReveal userCode={passRes.userCode} brand={passEvent.brand} title={passEvent.title} brandLogo={passEvent.brandLogo} onClose={() => setPassReservationId(null)} data-id="element-1806" data-mp="w62rlhji5sdq" />;
    })()}

      {/* Redemption Modal Flow */}
      {activeReservation && <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-1807" data-mp="1ev96eth6dzu9">
          <div className="bg-white rounded-3xl w-full max-w-sm relative overflow-hidden" data-id="element-1808" data-mp="8fgjxrdeua49">
            <button onClick={closeRedemption} className="absolute top-4 right-4 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors z-10" data-id="element-1809" data-mp="3ej77ub668a5">
              <X className="w-5 h-5 text-gray-600" data-id="element-1810" data-mp="2vm390vxlqxfx" />
            </button>

            {/* Step 1: Confirmation */}
            {redemptionStep === 'confirm' && <div className="p-8 text-center" data-id="element-1811" data-mp="2mmg7890ynpmc">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1812" data-mp="2ml54mcj94nwt">
                  <Clock className="w-8 h-8 text-yellow-600" data-id="element-1813" data-mp="27w8so4e2qqqx" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1814" data-mp="2cptl7gwwesiq">
                  Ready to collect?
                </h2>
                <p className="text-gray-500 mb-8 leading-relaxed" data-id="element-1815" data-mp="1yv0x3u2y040j">
                  Only activate this when you are at the location.
                </p>
                <div className="space-y-3" data-id="element-1816" data-mp="1ozpircvg4ytb">
                  <Button onClick={confirmRedemption} className="w-full py-4 text-lg" data-id="element-1817" data-mp="1p10ld9d5o0iu">
                    Yes, I'm here
                  </Button>
                  <Button variant="secondary" onClick={closeRedemption} className="w-full" data-id="element-1818" data-mp="16adnx05beikz">
                    Not yet
                  </Button>
                </div>
              </div>}

            {/* Step 2: Redeem — adapts to the drop's verification method */}
            {redemptionStep === 'exchange' && <div className="p-8 text-center" data-id="element-1819" data-mp="1p9hum29dw09h">
                <div className="w-16 h-16 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-1820" data-mp="1p8qabnl8loyi">
                  <KeyRound className="w-8 h-8 text-brand-blue" data-id="element-1821" data-mp="232qzpqqatzm7" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1822" data-mp="1y2bp36xej67x">
                  {codeAllowed && qrAllowed ? 'Claim your catch' : qrAllowed ? 'Show this to staff' : 'Enter the drop code'}
                </h2>

                {/* QR path — let staff scan the user's reservation QR */}
                {qrAllowed && <div className="mb-6" data-id="element-1823" data-mp="xokxytvysx2l">
                    <p className="text-gray-500 mb-4 text-sm leading-relaxed" data-id="element-1824" data-mp="12l1mhp72tdg7">
                      Ask the staff to scan this code to unlock your reserved
                      catch.
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 inline-block" data-id="element-1825" data-mp="9xozjdx073md">
                      <img src={reservationQrSrc} alt="Your reservation QR code" width={180} height={180} className="w-[180px] h-[180px] object-contain mx-auto" data-id="element-1826" data-mp="ev764xnjt6r7" />
                      <p className="mt-2 font-mono text-lg font-bold tracking-widest text-gray-900" data-id="element-1827" data-mp="3nar05yw1mt7b">
                        {activeReservation?.userCode}
                      </p>
                    </div>
                  </div>}

                {/* Code path — enter the drop code handed over at pickup */}
                {codeAllowed && <form onSubmit={handleCodeSubmit} data-id="element-1828" data-mp="20f3w2qi5shkm">
                    {qrAllowed && <div className="flex items-center my-5" data-id="element-1829" data-mp="1vfzp86ipivxg">
                        <div className="flex-1 h-px bg-gray-200" data-id="element-1830" data-mp="1vfhiuh2bz1bt" />
                        <span className="px-3 text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-1831" data-mp="1qh69s8jv0uun">
                          or enter code
                        </span>
                        <div className="flex-1 h-px bg-gray-200" data-id="element-1832" data-mp="1hocs64gxxjdm" />
                      </div>}
                    {!qrAllowed && <p className="text-gray-500 mb-6 text-sm leading-relaxed" data-id="element-1833" data-mp="1cr2w45z1q9gt">
                        Ask the staff for the drop code at pickup and enter it
                        here to claim your reserved spot.
                      </p>}
                    <Input placeholder="DROP CODE" value={inputCode} onChange={e => {
              setInputCode(e.target.value.toUpperCase());
              if (codeError) setCodeError(false);
            }} className="text-center text-2xl tracking-widest uppercase font-mono bg-gray-50 border-gray-200 text-gray-900" autoFocus={!qrAllowed} data-id="element-1834" data-mp="11j8uoln1ie8x" />
                    {codeError && <p className="text-red-500 text-xs mt-2" data-id="element-1835" data-mp="ds3gsc0ntw19">
                        That code doesn't match this drop. Check with the staff.
                      </p>}
                    <Button type="submit" className="w-full mt-6" data-id="element-1836" data-mp="3wtdzm2ftkmf">
                      Claim my catch
                    </Button>
                  </form>}

                <Button type="button" variant="secondary" onClick={closeRedemption} className="w-full mt-3" data-id="element-1837" data-mp="3r0zfk2ubiuj7">
                  {codeAllowed ? 'Cancel' : 'Done'}
                </Button>
              </div>}

            {/* Step 3: Success */}
            {redemptionStep === 'success' && <div className="p-8 text-center" data-id="element-1838" data-mp="2khx11inr94t3">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-1839" data-mp="2kfkhx269goeg">
                  <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-1840" data-mp="1rszirfjjn80c" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1841" data-mp="1rqn5hfoqdltm">
                  Catch claimed!
                </h2>
                <p className="text-gray-500 mb-6 text-sm leading-relaxed" data-id="element-1842" data-mp="21jcdg0l2ohk7">
                  Your reserved spot is now a catch. Review it to unlock the
                  next drop.
                </p>
                <div className="space-y-3" data-id="element-1843" data-mp="191zwim3focar">
                  <Button onClick={() => {
              closeRedemption();
              onNavigate('review-flow');
            }} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none" data-id="element-1844" data-mp="190ow8w975moe">
                    <Star className="w-4 h-4 mr-2" data-id="element-1845" data-mp="2l57o8kaqjfw7" />
                    Review Now
                  </Button>
                  <Button variant="secondary" onClick={closeRedemption} className="w-full" data-id="element-1846" data-mp="2h4xfx0zh0de6">
                    Later
                  </Button>
                </div>
              </div>}
          </div>
        </div>}
    </div>;
}